import {
  DISCARD_REASONS,
  DISCARD_REASON_LABELS,
  type DiscardReason,
  HANDOVER_TYPE_LABELS,
  type HandoverDocument,
  handoverDocumentSchema,
} from '@sivan/shared';
import { type SyntheticEvent, useEffect, useId, useState } from 'react';
import { ApiError, NetworkError, apiPost } from '../api/client';
import { Modal } from '../master-data/Modal';
import { Icon } from '../ui/Icon';
import { apartmentTitle } from './common';

/** Whether the browser reports a connection (it cannot prove the server is reachable). */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => {
      setOnline(navigator.onLine);
    };
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

function explain(error: unknown): string {
  if (error instanceof NetworkError)
    return 'אין חיבור לשרת, והטיוטה לא נמחקה. נסו שוב כשיהיה חיבור.';
  if (error instanceof ApiError) {
    const conflict = (error.body['conflict'] as { code?: string } | undefined)?.code;
    if (conflict === 'not_draft') return 'המסירה כבר אינה טיוטה, ולכן אי אפשר למחוק אותה.';
    if (conflict === 'released_defects')
      return 'בטיוטה יש ליקויים ששוחררו לטיפול קבלן ועדיין לא אושרו. בטלו את השחרור בניהול ליקויים (או חכו לאישור התיקון), ואז אפשר למחוק את הטיוטה.';
    if (error.status === 409)
      return 'הטיוטה השתנתה בינתיים (אולי במכשיר אחר). רעננו את המסירה ובדקו אותה לפני המחיקה.';
    if (error.status === 422 && error.body['fields'] !== undefined)
      return 'אי אפשר למחוק את הטיוטה: מסירה סופית של הדירה כבר מבוססת עליה.';
    if (error.status === 403) return 'אין לך הרשאה למחוק טיוטות בפרויקט הזה.';
  }
  return 'מחיקת הטיוטה נכשלה. הטיוטה לא השתנתה.';
}

/**
 * Discarding a draft opened by mistake, after an explicit choice in the application's own dialog:
 * what will be lost (findings, photos, unsaved changes on this device), a reason, and a red
 * button. Needs a connection: the server checks the permission and the status, keeps the record
 * and its history, and frees the apartment for a new draft.
 */
export function DiscardDraftDialog({
  doc,
  unsaved,
  open,
  onClose,
  onDiscarded,
}: {
  readonly doc: HandoverDocument;
  /** Changes on this device that were not saved on the server: they are lost too. */
  readonly unsaved: boolean;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onDiscarded: (doc: HandoverDocument) => void;
}) {
  const online = useOnline();
  const [reason, setReason] = useState<DiscardReason | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noteId = useId();
  const photos =
    doc.findings.reduce((n, f) => n + f.photos.length, 0) +
    (doc.reinspection?.items.reduce((n, r) => n + r.photos.length, 0) ?? 0);
  const ready = reason !== null && (reason !== 'other' || note.trim() !== '');
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready || busy || !online) return;
    setBusy(true);
    setError(null);
    apiPost(
      `/projects/${doc.project.id}/handovers/${doc.id}/discard`,
      {
        rowVersion: doc.rowVersion,
        reason,
        ...(reason === 'other' ? { note: note.trim() } : {}),
      },
      handoverDocumentSchema,
    )
      .then(onDiscarded)
      .catch((e: unknown) => {
        setError(explain(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <Modal title="מחיקת טיוטה" open={open} onClose={onClose}>
      {open && (
        <form className="discard-draft" onSubmit={submit}>
          <p className="discard-draft__subject">
            {HANDOVER_TYPE_LABELS[doc.type]} · {apartmentTitle(doc.apartment)} · פרוטוקול{' '}
            <bdi dir="ltr">{doc.protocolNumber}</bdi>
          </p>
          <ul className="discard-draft__loss">
            <li>
              {doc.findings.length === 0
                ? 'אין ליקויים בטיוטה.'
                : doc.findings.length === 1
                  ? 'ליקוי אחד יוסר מהטיוטה.'
                  : `${String(doc.findings.length)} ליקויים יוסרו מהטיוטה.`}
            </li>
            {photos > 0 && (
              <li>{photos === 1 ? 'תמונה אחת' : `${String(photos)} תמונות`} יוסרו מהטיוטה.</li>
            )}
            {unsaved && (
              <li className="discard-draft__unsaved">
                <strong>יש במכשיר הזה שינויים שעדיין לא נשמרו בשרת. גם הם יימחקו.</strong>
              </li>
            )}
            <li>
              הטיוטה תוסר מרשימת המסירות, והדירה תהיה פנויה לטיוטה חדשה. הרישום וההיסטוריה נשמרים
              במערכת, ומספר הפרוטוקול לא ינוצל שוב.
            </li>
          </ul>
          <fieldset className="discard-draft__reasons">
            <legend>למה מוחקים?</legend>
            {DISCARD_REASONS.map((r) => (
              <label key={r} className="choice">
                <input
                  type="radio"
                  name="reason"
                  value={r}
                  checked={reason === r}
                  onChange={() => {
                    setReason(r);
                  }}
                />
                <span>{DISCARD_REASON_LABELS[r]}</span>
              </label>
            ))}
          </fieldset>
          {reason === 'other' && (
            <label className="field" htmlFor={noteId}>
              <span>פירוט הסיבה</span>
              <textarea
                id={noteId}
                rows={2}
                maxLength={300}
                value={note}
                required
                onChange={(e) => {
                  setNote(e.target.value);
                }}
              />
            </label>
          )}
          {!online && (
            <p className="notice notice--warning" role="status">
              <Icon name="cloud-off" /> מחיקה דורשת חיבור. הטיוטה לא תימחק בלי חיבור לשרת.
            </p>
          )}
          {error !== null && (
            <p className="notice notice--error" role="alert">
              {error}
            </p>
          )}
          <div className="confirm-dialog__actions">
            <button
              type="submit"
              className="button button--danger"
              disabled={!ready || busy || !online}
            >
              {busy ? 'מוחק…' : 'מחיקת הטיוטה'}
            </button>
            <button type="button" className="button" onClick={onClose} autoFocus>
              ביטול
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/** A discarded draft: what happened, and nothing to edit. */
export function DiscardedNotice({
  doc,
  keptLocally,
  onForgetLocal,
  onBack,
}: {
  readonly doc: HandoverDocument;
  /** Unsaved changes from this device that were kept (never removed without asking). */
  readonly keptLocally: boolean;
  readonly onForgetLocal: () => void;
  readonly onBack: () => void;
}) {
  return (
    <section className="discarded-notice" aria-label="טיוטה שנמחקה">
      <h2>הטיוטה נמחקה</h2>
      <p>
        {HANDOVER_TYPE_LABELS[doc.type]} · {apartmentTitle(doc.apartment)} · פרוטוקול{' '}
        <bdi dir="ltr">{doc.protocolNumber}</bdi>
        {doc.discarded === null
          ? ''
          : ` · ${DISCARD_REASON_LABELS[doc.discarded.reason]} · ${new Date(doc.discarded.at).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', dateStyle: 'short', timeStyle: 'short' })}`}
      </p>
      <p className="hint">אפשר לפתוח לדירה טיוטה חדשה. הרישום וההיסטוריה נשמרים במערכת.</p>
      {keptLocally && (
        <div className="notice notice--warning">
          <p>
            במכשיר הזה נשארו שינויים לטיוטה הזו שלא נשמרו בשרת לפני שנמחקה. הם לא יישלחו, ונשמרים
            כאן עד שתבחרו למחוק אותם.
          </p>
          <button type="button" className="button" onClick={onForgetLocal}>
            מחיקת השינויים מהמכשיר
          </button>
        </div>
      )}
      <button type="button" className="button button--primary" onClick={onBack}>
        חזרה לדירה
      </button>
    </section>
  );
}
